// 📄 catalogoBadisService.ts
// Catálogo global de BAdIs y Objetos ABAP (BAdIs estándar, implementaciones
// Z, clases, programas, módulos de función e interfaces/proxies) usados en
// las integraciones SAP Ariba / S/4HANA. Referencia técnica transversal a
// todos los proyectos — mismo criterio que catalogoApisService.ts: CRUD
// completo solo desde el panel Admin, lectura abierta a cualquier
// consultor autenticado (ver firestore.rules, colección `catalogoBadis`).
import {
  doc,
  collection,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';

export type SolucionBadi = 'commerce' | 'buying' | 'sourcing' | 'slp' | 'masterdata' | 'general';

export interface SolucionBadiMeta {
  id: SolucionBadi;
  label: string;
}

export const SOLUCIONES_BADI: SolucionBadiMeta[] = [
  { id: 'commerce', label: 'Commerce Automation' },
  { id: 'buying', label: 'Buying' },
  { id: 'sourcing', label: 'Sourcing' },
  { id: 'slp', label: 'SLP' },
  { id: 'masterdata', label: 'Master Data' },
  { id: 'general', label: 'General' }
];

// "programa"/Include se absorbió al catálogo dedicado de Programas (ver
// catalogoProgramasService.ts) — auditado 2026-08-12: 0 registros reales en
// QAS/PRD usaban este tipo, así que no hizo falta ninguna migración de
// datos, solo sacarlo de las opciones.
export type TipoObjetoAbap = 'badi' | 'clase' | 'funcion' | 'interfaz';

export interface TipoObjetoAbapMeta {
  id: TipoObjetoAbap;
  label: string;
  // Transacción SAP sugerida al elegir este tipo — el campo `transaccion`
  // sigue siendo texto libre (algunos objetos usan más de una, ver
  // ARBCIG_ERP_SES_CREATE: SE18 + SE38), esto es solo la sugerencia inicial.
  transaccionSugerida: string;
}

export const TIPOS_OBJETO_ABAP: TipoObjetoAbapMeta[] = [
  { id: 'badi', label: 'BAdI', transaccionSugerida: 'SE18' },
  { id: 'clase', label: 'Clase ABAP', transaccionSugerida: 'SE24' },
  { id: 'funcion', label: 'Módulo de Funciones', transaccionSugerida: 'SE37' },
  { id: 'interfaz', label: 'Interfaz / Proxy', transaccionSugerida: 'SPROXY' }
];

export const tipoObjetoMeta = (tipo: TipoObjetoAbap): TipoObjetoAbapMeta =>
  TIPOS_OBJETO_ABAP.find((t) => t.id === tipo) || TIPOS_OBJETO_ABAP[0];

export const solucionBadiMeta = (solucion: SolucionBadi): SolucionBadiMeta =>
  SOLUCIONES_BADI.find((s) => s.id === solucion) || SOLUCIONES_BADI[SOLUCIONES_BADI.length - 1];

export interface BadiObjetoData {
  nombre: string;
  solucion: SolucionBadi;
  tipo: TipoObjetoAbap;
  transaccion: string;
  implementacionZ: string;
  claseImplementacion: string;
  metodoInterfaz: string;
  programaInclude: string;
  descripcion: string;
}

export interface BadiObjeto extends BadiObjetoData {
  id: string;
  createdAt?: any;
  updatedAt?: any;
}

const CATALOGO_COLLECTION = "catalogoBadis";

export const getBadis = async (): Promise<BadiObjeto[]> => {
  const snap = await getDocs(collection(db, CATALOGO_COLLECTION));
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      nombre: data.nombre || '',
      solucion: (data.solucion as SolucionBadi) || 'general',
      tipo: (data.tipo as TipoObjetoAbap) || 'badi',
      transaccion: data.transaccion || '',
      implementacionZ: data.implementacionZ || '',
      claseImplementacion: data.claseImplementacion || '',
      metodoInterfaz: data.metodoInterfaz || '',
      programaInclude: data.programaInclude || '',
      descripcion: data.descripcion || '',
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    };
  });
};

/**
 * Crea (badiId = null) o actualiza (badiId = id existente) un registro.
 * Igual que catalogoApisService.ts, el id lo genera el llamador cuando es
 * nuevo (ver generarId en BadiCatalogSection.tsx) para poder tener una
 * tarjeta local antes del primer guardado.
 */
export const saveBadi = async (badiId: string, data: BadiObjetoData, esNuevo: boolean): Promise<void> => {
  if (!data.nombre.trim()) throw new Error("El nombre del objeto/BAdI es obligatorio.");

  const payload: Record<string, any> = {
    nombre: data.nombre.trim(),
    solucion: data.solucion,
    tipo: data.tipo,
    transaccion: data.transaccion.trim(),
    implementacionZ: data.implementacionZ.trim(),
    claseImplementacion: data.claseImplementacion.trim(),
    metodoInterfaz: data.metodoInterfaz.trim(),
    programaInclude: data.programaInclude.trim(),
    descripcion: data.descripcion.trim(),
    updatedAt: serverTimestamp()
  };
  if (esNuevo) payload.createdAt = serverTimestamp();

  await setDoc(doc(db, CATALOGO_COLLECTION, badiId), payload, { merge: true });
};

export const deleteBadi = async (badiId: string): Promise<void> => {
  await deleteDoc(doc(db, CATALOGO_COLLECTION, badiId));
};

// ── Sembrado inicial ──────────────────────────────────────────────────
// Ids determinísticos (a partir del nombre técnico) para que sembrar sea
// idempotente: correrlo más de una vez pisa los mismos 9 registros en vez
// de duplicarlos.
const slugificarId = (nombre: string) =>
  nombre.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const SEED_BADIS: BadiObjetoData[] = [
  {
    nombre: 'ARBCIG_PURREQ - BAdI de PR de Bajada',
    solucion: 'buying',
    tipo: 'badi',
    transaccion: 'SE18',
    implementacionZ: '',
    claseImplementacion: '',
    metodoInterfaz: 'IF_ARBCIG_PURREQ~PREPROCESSING',
    programaInclude: '',
    descripcion: 'BAdI estándar para el preprocesamiento de Requisiciones de Compra (PR) de bajada hacia Ariba/CIG.'
  },
  {
    nombre: 'ARBCIG_BUY_PURCHORD_BADI - BAdI PO Buying',
    solucion: 'buying',
    tipo: 'badi',
    transaccion: 'SE18',
    implementacionZ: '',
    claseImplementacion: '',
    metodoInterfaz: 'IF_ARBCIG_BUY_PURCHORD~PUBLISH_PO_CREATE',
    programaInclude: '',
    descripcion: 'BAdI para publicar la creación de la Orden de Compra (PO) generada desde Ariba Buying.'
  },
  {
    nombre: 'ARBCIG_ERP_SES_CREATE - HES desde ERP',
    solucion: 'commerce',
    tipo: 'badi',
    transaccion: 'SE18 / SE38',
    implementacionZ: '',
    claseImplementacion: '',
    metodoInterfaz: '',
    programaInclude: 'LMLSRF0F',
    descripcion: 'BAdI y programa/include asociados a la creación de Hojas de Entrada de Servicio (HES) desde el ERP.'
  },
  {
    nombre: 'ARBCIG_RECPT_UPDATE - Recepciones MIGO',
    solucion: 'commerce',
    tipo: 'badi',
    transaccion: 'SE18',
    implementacionZ: '',
    claseImplementacion: '',
    metodoInterfaz: 'IF_EX_ARBCIG_RECEIPT_UPDATE~UPDATE_RECEIPT_DETAILS',
    programaInclude: '',
    descripcion: 'BAdI para actualizar el detalle de recepciones de mercancía (MIGO) hacia Ariba/CIG.'
  },
  {
    nombre: 'ARBCIG_INVOICESTATUSUPDATE - Status Invoice',
    solucion: 'commerce',
    tipo: 'badi',
    transaccion: 'SE18',
    implementacionZ: '',
    claseImplementacion: '',
    metodoInterfaz: '',
    programaInclude: '',
    descripcion: 'BAdI para la actualización del estatus de facturas (Invoice Status) hacia SAP Ariba.'
  },
  {
    nombre: 'DespatchedDeliveryNotification_Async_In - Interfaz ASN',
    solucion: 'commerce',
    tipo: 'interfaz',
    transaccion: 'SPROXY',
    implementacionZ: '',
    claseImplementacion: '',
    metodoInterfaz: '',
    programaInclude: 'DespatchedDeliveryNotification_Async_In',
    descripcion: 'Interfaz/proxy receptora del Aviso de Expedición (ASN) asíncrono.'
  },
  {
    nombre: 'ARBCIG_SRC_QUOTE_PO - Sourcing PO',
    solucion: 'sourcing',
    tipo: 'badi',
    transaccion: 'SE18',
    implementacionZ: '',
    claseImplementacion: '',
    metodoInterfaz: '',
    programaInclude: '',
    descripcion: 'BAdI para la generación de la Orden de Compra a partir de la adjudicación de un evento de Sourcing. BAPI relacionada: BAPI_PO_CREATE1.'
  },
  {
    nombre: 'MDG_SE_BP_BULK_REPLRQ_IN - SLP BP',
    solucion: 'slp',
    tipo: 'interfaz',
    transaccion: 'SPROXY',
    implementacionZ: '',
    claseImplementacion: '',
    metodoInterfaz: '',
    programaInclude: '',
    descripcion: 'Interfaz/proxy de replicación masiva de Business Partner (proveedores) para SLP.'
  },
  {
    nombre: 'ARBCIG_MASTERDATA - Master Data',
    solucion: 'masterdata',
    tipo: 'badi',
    transaccion: 'SE18',
    implementacionZ: '',
    claseImplementacion: '',
    metodoInterfaz: '',
    programaInclude: '',
    descripcion: 'BAdI genérica de integración de datos maestros entre el ERP y Ariba.'
  }
];

/**
 * Carga los 9 registros de ejemplo (ver SEED_BADIS). Idempotente: usa un
 * id determinístico por nombre, así correrlo de nuevo actualiza los
 * mismos registros en vez de duplicarlos.
 */
export const seedCatalogoBadis = async (): Promise<void> => {
  await Promise.all(
    SEED_BADIS.map((data) => saveBadi(slugificarId(data.nombre), data, true))
  );
};
